/**
 * Duplicate-detection helpers for job creation & invoicing.
 *
 * Pure, side-effect-free functions operating on the rows returned by
 * GET /requestimatesVIN/:installerId/:vin. Each row carries:
 *   request_id, request_type, requestor_name, service_requested,
 *   current_state, location_id, locationName, created_date, due_date, assigned_to
 *
 * Matching is intentionally SERVICE-AGNOSTIC: any same-VIN job in the relevant
 * lifecycle bucket counts. The endpoint returns no master_service_id/category_id,
 * and the guards are block-with-confirm, so we err toward catching every dupe.
 *
 * State buckets (compared case-insensitively against current_state):
 *   TERMINAL      — dead rows, never a duplicate
 *   COMPLETED     — job already finished/invoiced/paid
 *   pre-completed — everything else (scheduled or in-progress somewhere)
 */

import moment from 'moment';

export const TERMINAL_STATES = ['cancelled', 'deleted', 'void'];
export const COMPLETED_STATES = ['finished', 'collected', 'reviewed', 'paid'];
export const DRAFT_STATES = ['draft'];

/** Trim + uppercase a VIN for safe comparison / URL use. */
export function normalizeVin(vin) {
  return String(vin || '').trim().toUpperCase();
}

/**
 * Placeholder VINs (not-yet-collected / unknown) must NOT trigger the check —
 * they would falsely match every other placeholder job. Empty counts too.
 */
export function isPlaceholderVin(vin) {
  const v = normalizeVin(vin);
  return v === '' || v.startsWith('NV') || v.startsWith('OT');
}

function stateOf(row) {
  return String((row && row.current_state) || '').trim().toLowerCase();
}

export function isTerminalState(state) {
  return TERMINAL_STATES.includes(String(state || '').trim().toLowerCase());
}

export function isCompletedState(state) {
  return COMPLETED_STATES.includes(String(state || '').trim().toLowerCase());
}

/** Parked, not-yet-submitted request — resumable, never a blocking duplicate. */
export function isDraftState(state) {
  return DRAFT_STATES.includes(String(state || '').trim().toLowerCase());
}

/**
 * Not terminal, not completed, and not a draft → the vehicle is scheduled or in
 * a shop. Drafts are excluded: they're parked work the user can resume, not an
 * open job that should block re-creation.
 */
export function isPreCompletedState(state) {
  return !isTerminalState(state) && !isCompletedState(state) && !isDraftState(state);
}

/**
 * Guard 2 (creation): rows for this VIN that are pre-completed (open/in-flight).
 * Service-agnostic.
 */
export function findPreCompletedForVin(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => isPreCompletedState(stateOf(row)));
}

/**
 * Parked drafts for this VIN. These are not blocking duplicates — the UI can
 * offer to resume them instead of starting a fresh request. Service-agnostic.
 */
export function findResumableDrafts(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => isDraftState(stateOf(row)));
}

/**
 * Guard 3 (invoice): OTHER rows for this VIN (different request_id) that are
 * already completed/invoiced. Service-agnostic.
 */
export function findCompletedOtherForVin(rows, excludeRequestId) {
  if (!Array.isArray(rows)) return [];
  // request_id may arrive as number or string — compare loosely.
  const exclude = excludeRequestId == null ? null : String(excludeRequestId);
  return rows.filter(
    (row) =>
      isCompletedState(stateOf(row)) &&
      (exclude == null || String(row.request_id) !== exclude)
  );
}

// ---------------------------------------------------------------------------
// 409 (duplicate open job) messaging — used by the create forms when the
// server rejects POST /requestimates/ with a conflict.
// ---------------------------------------------------------------------------

/**
 * Pull existing-job rows out of a 409 response body. Tolerant of shape:
 * accepts { matches: [...] } / { existing: {...} } / { requests: [...] } /
 * a bare array / a bare row object. Field names are normalized downstream.
 */
export function extractRowsFrom409(data) {
  if (!data) return [];
  const candidate =
    data.matches || data.existing || data.requests || data.duplicates || data;
  if (Array.isArray(candidate)) return candidate;
  if (candidate && (candidate.request_id || candidate.requestId)) return [candidate];
  return [];
}

const rowField = (row, snake, camel) => (row && (row[snake] != null ? row[snake] : row[camel])) || null;

const titleCase = (s) => {
  const v = String(s || '').trim();
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : '';
};

/**
 * One summary line for an existing open job:
 *   #{requestId} — {serviceRequested} · {currentState} · started {createdDate} · {locationName}
 * Missing fields are dropped, not rendered as blanks.
 */
export function formatOpenJobLine(row) {
  const requestId = rowField(row, 'request_id', 'requestId');
  const service = rowField(row, 'service_requested', 'serviceRequested');
  const state = titleCase(rowField(row, 'current_state', 'currentState'));
  const created = rowField(row, 'created_date', 'createdDate');
  const location = rowField(row, 'locationName', 'location_name');

  const m = created ? moment(created) : null;
  const createdLabel = m && m.isValid() ? m.format('MMM D, YYYY') : null;

  const parts = [
    service,
    state,
    createdLabel ? `started ${createdLabel}` : null,
    location,
  ].filter(Boolean);

  return `#${requestId}${parts.length ? ' — ' + parts.join(' · ') : ''}`;
}

/**
 * Full user-facing message for a create-time 409.
 *   The {year} {make} {model} already has an open job with you:
 *
 *   #{requestId} — {serviceRequested} · {currentState} · started {createdDate} · {locationName}
 * Multiple open jobs render one line each. With no row data (server body
 * didn't include it and the fallback lookup failed) a generic line is used.
 */
export function buildOpenJobMessage(vehicle, rows) {
  const desc = [vehicle && vehicle.year, vehicle && vehicle.make, vehicle && vehicle.model]
    .filter(Boolean).join(' ');
  const intro = `${desc ? `The ${desc}` : 'This vehicle'} already has an open job with you:`;

  const list = (Array.isArray(rows) ? rows : []).filter((r) => rowField(r, 'request_id', 'requestId'));
  if (!list.length) {
    return `${desc ? `The ${desc}` : 'This vehicle'} already has an open job with you. Check the open jobs list before creating a new request.`;
  }
  return `${intro}\n\n${list.map(formatOpenJobLine).join('\n')}`;
}
